import { AppConst } from 'src/app/shared/constants/app-const';
import { UrlConst } from 'src/app/shared/constants/url-const';
import { Sensor } from 'src/app/shared/interfaces/sensor';
import { Site } from 'src/app/shared/interfaces/site';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { UtilFunctions } from 'src/app/shared/utils/util-functions';

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SensorListTableService } from '../sensor-list-table/sensor-list-table.service';
import { SensorTiltFormService } from './sensor-tilt-form.service';
import { SiteService } from 'src/app/shared/services/site.service';

@Component({
  selector: 'app-sensor-tilt-form',
  templateUrl: './sensor-tilt-form.component.html',
  styleUrls: ['./sensor-tilt-form.component.scss'],
})
export class SensorTiltFormComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private sensorTiltFormService = inject(SensorTiltFormService);
  private sensorListTableService = inject(SensorListTableService);
  private siteService = inject(SiteService);
  private snackBar = inject(MatSnackBar);

  @Input() sensor: Sensor | null = null;
  @Output() registered = new EventEmitter<boolean>();
  @Output() canceled = new EventEmitter<boolean>();

  errors: string[] = [];
  siteId: string | null = null;
  site: Site | null = null;
  sensors: Sensor[] | null = null;
  selectedSensor: Sensor | null = null;
  sensorCount: number = 0;

  isUpdate: boolean = false;
  isZeroSet: boolean = false;
  originalStandard: {
    tiltX: number;
    tiltY: number;
    date?: string;
  } | null = null;

  form = this.formBuilder.group({
    sensorId: ['', [Validators.required]],
    positionName: ['', [Validators.required]],
    heightFromBottom: ['', [Validators.required]],
    managementStandard: ['', [Validators.required]],
    hourlyDisplacementStandard: ['', [Validators.required]],
    standardX: ['', [Validators.required]],
    standardY: ['', [Validators.required]],
  });
  get sensorId() {
    return this.form.get('sensorId');
  }
  get positionName() {
    return this.form.get('positionName');
  }
  get heightFromBottom() {
    return this.form.get('heightFromBottom');
  }
  get managementStandard() {
    return this.form.get('managementStandard');
  }
  get hourlyDisplacementStandard() {
    return this.form.get('hourlyDisplacementStandard');
  }
  get standardX() {
    return this.form.get('standardX');
  }
  get standardY() {
    return this.form.get('standardY');
  }

  async ngOnInit() {
    this.sensorTiltFormService.addLog();

    this.route.paramMap.subscribe((params) => (this.siteId = params.get('id')));

    // 管理者か一般ユーザのみアクセス可能
    if (!this.sensorTiltFormService.isValidUser()) {
      console.log('user is not sysadmin or general');
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
      return;
    }

    // 一般ユーザの場合は、自分のサイトのみアクセス可能
    if (this.sensorTiltFormService.isGeneralGroup()) {
      console.log('user is general');
      const account = this.sensorTiltFormService.getUser();
      if (account === null) {
        console.log('account is null');
        this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
        return;
      }
      if (this.siteId !== account.user) {
        console.log('siteId != user');
        this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
        return;
      }
    }

    // サイトIDが指定されていない場合は、サイト一覧に戻る
    if (this.siteId === null) {
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SITE_CONFIG]);
      return;
    }

    // サイト情報取得
    this.site = await this.sensorTiltFormService.getSiteWithOrganization(
      this.siteId
    );

    // サイトが取得できなかった場合は、サイト一覧に戻る
    if (this.site === null) {
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SITE_CONFIG]);
      return;
    }

    this.sensorCount = this.siteService.countTiltSensors(this.site);

    // フォームのプルダウンリスト作成（未使用センサー）
    const sensors: Sensor[] | null =
      await this.sensorTiltFormService.getNoConfigSensorsWithOrganization();

    this.sensors = sensors;

    // センサーが指定されていたら更新モード
    if (
      this.sensor !== null &&
      this.sensor.id !== undefined &&
      this.sensors !== null
    ) {
      this.isUpdate = true;
      this.selectedSensor = this.sensor;

      // 元のstandard値を保存
      if (this.sensor.standard) {
        this.originalStandard = {
          tiltX: this.sensor.standard.tiltX,
          tiltY: this.sensor.standard.tiltY,
        };
        // dateが存在する場合は保持
        if (this.sensor.standard.date) {
          this.originalStandard.date = this.sensor.standard.date;
        }
      }

      // 未使用センサーに今のセンサーを追加する
      const usedSensor = await this.sensorTiltFormService.getSensor(
        this.sensor.id
      );
      this.sensors.unshift(usedSensor!);

      this.form.setValue({
        sensorId: this.sensor.id,
        positionName: this.sensor.positionName ?? '',
        heightFromBottom: this.sensor.heightFromBottom ?? '',
        managementStandard: this.sensor.managementStandard ?? '',
        hourlyDisplacementStandard:
          this.sensor.hourlyDisplacementStandard ?? '',
        standardX: this.sensor.standard?.tiltX ?? '',
        standardY: this.sensor.standard?.tiltY ?? '',
      });
    } else {
      this.isUpdate = false;
      this.form.patchValue({
        positionName: `山留め-${this.sensorCount + 1}`,
      });
    }
  }

  async onSubmit() {
    try {
      this.loadingService.startLoading();

      // 更新でIDが変わっている場合は一旦古いセンサーを削除する
      if (this.isUpdate) {
        if (this.sensor === null) {
          this.registered.emit(false);
          return;
        }

        // センサー変更の時は一旦今のセンサー情報を削除して、後に控える登録処理を実行する
        if (this.sensorId?.value !== this.sensor.id) {
          try {
            await this.sensorListTableService.deleteSensor(
              this.site!,
              this.sensor!
            );
          } catch (error) {
            console.log('SiteConfigPageComponent.deleteConfig(): ', error);
            return;
          }
        }
      }

      // センサー情報の設定（基準値と閾値を含む）
      const newStandardData = {
        tiltX: this.parseFloatSafe(this.standardX?.value),
        tiltY: this.parseFloatSafe(this.standardY?.value),
      };

      // standardの変更をチェック
      let standardData: any = undefined;
      let isStandardUpdated = false;

      if (this.isUpdate && this.originalStandard) {
        // 更新モードで元のstandardがある場合
        const hasStandardChanged =
          this.originalStandard.tiltX !== newStandardData.tiltX ||
          this.originalStandard.tiltY !== newStandardData.tiltY;

        if (hasStandardChanged || this.isZeroSet) {
          // 変更があった場合は、新しい値と変更日時を設定
          standardData = {
            ...newStandardData,
            date: UtilFunctions.now(),
          };
          isStandardUpdated = true;
        } else {
          // 変更がない場合は元のデータをそのまま使用
          standardData = this.originalStandard;
        }
      } else {
        // 新規登録の場合は、新しい値と日時を設定
        standardData = {
          ...newStandardData,
          date: UtilFunctions.now(),
        };
        isStandardUpdated = true;
      }

      await this.sensorTiltFormService.setSensorToSite(
        this.site!,
        this.sensorId?.value,
        this.positionName?.value,
        this.parseFloatSafe(this.heightFromBottom?.value),
        this.parseFloatSafe(this.managementStandard?.value),
        this.parseFloatSafe(this.hourlyDisplacementStandard?.value),
        standardData,
        undefined,
        this.isUpdate,
        isStandardUpdated
      );

      this.registered.emit(true);
      if (this.isUpdate === true) {
        this.isUpdate = false;
        this.sensor = null;
        this.originalStandard = null;
      }
      this.isZeroSet = false;
      this.form.reset({
        sensorId: '',
        positionName: '',
        standardX: '',
        standardY: '',
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingService.stopLoading();
    }
  }

  async onSensorSelected(sensorId: string) {
    this.isZeroSet = false;
    if (!sensorId || sensorId === 'undefined') {
      // センサーが未選択の場合、フォームをクリア
      this.selectedSensor = null;
      this.originalStandard = null;
      this.form.patchValue({
        standardX: '',
        standardY: '',
      });
      return;
    }

    try {
      this.loadingService.startLoading();

      // 選択されたセンサーの詳細情報を取得
      this.selectedSensor = await this.sensorTiltFormService.getSensor(
        sensorId
      );

      if (this.selectedSensor) {
        let standardX: number | string = '';
        let standardY: number | string = '';

        // 基準値の設定: standardプロパティがあればそれを採用、なければlatestDataから採用
        if (this.selectedSensor.standard) {
          standardX = this.selectedSensor.standard.tiltX;
          standardY = this.selectedSensor.standard.tiltY;

          // 既存のstandardがある場合は、originalStandardとして保存
          this.originalStandard = {
            tiltX: this.selectedSensor.standard.tiltX,
            tiltY: this.selectedSensor.standard.tiltY,
          };
          if (this.selectedSensor.standard.date) {
            this.originalStandard.date = this.selectedSensor.standard.date;
          }
        } else if (this.selectedSensor.latestData) {
          standardX = this.selectedSensor.latestData.tiltX ?? '';
          standardY = this.selectedSensor.latestData.tiltY ?? '';

          // standardがない場合は、originalStandardはnull
          this.originalStandard = null;
        }

        // フォームに値を設定
        this.form.patchValue({
          standardX: standardX,
          standardY: standardY,
        });
      }
    } catch (error) {
      console.error('センサーデータの取得に失敗しました:', error);
    } finally {
      this.loadingService.stopLoading();
    }
  }

  private parseFloatSafe(value: string | number | null | undefined): number {
    if (value === null || value === undefined) {
      return 0;
    }

    // 既に数値の場合はそのまま返す
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }

    // 文字列の場合
    if (typeof value === 'string' && value.trim() === '') {
      return 0;
    }

    const parsed = parseFloat(value.toString());
    return isNaN(parsed) ? 0 : parsed;
  }

  onZeroSet() {
    if (this.selectedSensor) {
      if (this.selectedSensor.latestData) {
        this.isZeroSet = true;
        this.form.patchValue({
          standardX: this.selectedSensor.latestData.tiltX ?? '',
          standardY: this.selectedSensor.latestData.tiltY ?? '',
        });
        this.snackBar.open(
          '最新データから基準値を設定しました。「登録」ボタンで保存してください',
          '閉じる',
          { duration: 3000 }
        );
      } else {
        this.form.patchValue({
          standardX: '',
          standardY: '',
        });
        this.snackBar.open('最新データが存在しません', '閉じる', {
          duration: 3000,
        });
      }
    }
  }

  cancel() {
    this.isUpdate = false;
    this.sensor = null;
    this.originalStandard = null;
    this.isZeroSet = false;
    this.form.reset({
      sensorId: '',
      positionName: '',
      standardX: '',
      standardY: '',
    });
    this.canceled.emit(true);
  }
}
